import { Controller } from "@hotwired/stimulus"
import echarts from "echarts/lib/echarts"
import "echarts/lib/chart/line"
import "echarts/lib/chart/scatter"
import "echarts/lib/chart/custom"
import "echarts/lib/component/legend"
import "echarts/lib/component/markArea"
import "echarts/lib/component/grid"
import "echarts/lib/component/dataZoom"
import "echarts/lib/component/tooltip"
import "echarts/lib/component/axisPointer"
import moment from "moment"
import flatpickr from "flatpickr"
import helpers from "../globals/helpers"
import constants from "../globals/constants"

const DASHBOARD_COLORS = {
  POWER_PRODUCTION: constants.COLOR_ENERGY_PRODUCED,
  POWER_GRID: constants.COLOR_POWER_GRID,
  POWER_STORAGE: constants.COLOR_POWER_STORAGES,
  POWER_CONSUMPTION: constants.COLOR_ENERGY_CONSUMED,
  STATE_OF_CHARGE: constants.COLOR_STATE_OF_CHARGE
}

const RELEVANT_SERIES = [
  "ACTIVE_POWER", "VOLTAGE", "CURRENT", "POWER", "REACTIVE_POWER", "APPARENT_POWER",
  "FREQUENCY", "FLOW_TEMPERATURE", "RETURN_TEMPERATURE", "FLOWED_VOLUME"
]

const makeYAxis = (id, position, formatter = null) => ({
  id,
  type: "value",
  scale: true,
  splitLine: { show: false },
  position,
  axisLine: { show: false },
  axisTick: { show: false },
  ...(formatter != null ? { axisLabel: { formatter } } : {})
})

export default class TimelineChartController extends Controller {
  static targets = [
    "chart",
    "noDataText",
    "dateRangePicker",
    "prevButton",
    "nextButton"
  ]

  static values = {
    siteId: String,
    thingId: String,
    capabilityId: String,
    externalPlantControlId: String,
    extraQueryParams: { type: String, default: "" },
    showChargingCycles: { type: Boolean, default: false }
  }

  connect() {
    this.initializeState()
    this.initializeDateRangePicker()
    this.initializeChart()
    this.requestData()
  }

  disconnect() {
    this.abortController.abort()
    this.stopPolling()
    this.flatpickrInstance?.destroy()
    this.disposeChart()
  }

  initializeState() {
    this.initialLoadComplete = false
    this.pollingTimerId = null
    this.hasChargingCycles = false
    this.yAxes = []
    this.extraValues = {}
    this.options = this.buildChartOptions()
    this.abortController = new AbortController()
  }

  initializeDateRangePicker() {
    this.fromDate = moment()
    this.toDate = moment()

    if (!this.hasDateRangePickerTarget) return

    this.flatpickrInstance = flatpickr(this.dateRangePickerTarget, {
      mode: "range",
      dateFormat: "d.m.Y",
      maxDate: "today",
      locale: { rangeSeparator: " \u2014 " },
      onChange: (selectedDates) => this.dateRangeChanged(selectedDates),
      onClose: () => this.handleDatePickerClose(),
      onReady: (_selectedDates, _dateStr, instance) => {
        const todayBtn = document.createElement("button")
        todayBtn.className = "btn btn-sm btn-link flatpickr-today-button"
        todayBtn.type = "button"
        todayBtn.textContent = helpers.t("vue.date_range_picker.today")
        todayBtn.addEventListener("click", () => {
          this.resetToToday()
          instance.close()
        })
        instance.calendarContainer.appendChild(todayBtn)
      }
    })
    this.syncFlatpickr()
    this.updateNavigationState()
  }

  resetToToday() {
    this.fromDate = moment()
    this.toDate = moment()
    this.syncFlatpickr()
    this.updateNavigationState()
    this.requestData()
  }

  initializeChart() {
    if (!this.hasChartTarget) {
      return
    }

    this.chart = echarts.init(this.chartTarget)
    this.legendChangedHandler = (event) => this.legendChanged(event)
    this.chart.on("legendselectchanged", this.legendChangedHandler)

    this.resizeObserver = new ResizeObserver(() => this.chart?.resize())
    this.resizeObserver.observe(this.chartTarget)
  }

  disposeChart() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (!this.chart) {
      return
    }

    if (this.legendChangedHandler) {
      this.chart.off("legendselectchanged", this.legendChangedHandler)
    }

    this.chart.dispose()
    this.chart = null
  }

  get remoteUrl() {
    if (this.hasSiteIdValue) {
      return `/api/v1/sites/${this.siteIdValue}/ems?${this.remoteTimeRangeParams}`
    }

    if (this.hasCapabilityIdValue) {
      return `/api/v1/ems_capabilities/${this.capabilityIdValue}/metrics?${this.remoteTimeRangeParams}`
    }

    if (this.hasExternalPlantControlIdValue) {
      return `/api/v1/external_plant_controls/${this.externalPlantControlIdValue}/metrics?${this.remoteTimeRangeParams}`
    }

    if (this.hasThingIdValue) {
      return `/api/v1/things/${this.thingIdValue}/metrics?${this.remoteTimeRangeParams}`
    }

    return null
  }

  get isSingleDay() {
    return this.fromDate.isSame(this.toDate, "day")
  }

  get isToday() {
    return this.isSingleDay && this.toDate.isSame(moment(), "day")
  }

  get remoteTimeRangeParams() {
    const params = new URLSearchParams({
      from: this.fromDate.format("YYYY-MM-DD"),
      to: this.toDate.format("YYYY-MM-DD")
    })

    if (this.hasExtraQueryParamsValue && this.extraQueryParamsValue.length > 0) {
      const extraParams = new URLSearchParams(this.extraQueryParamsValue)
      extraParams.forEach((value, key) => params.append(key, value))
    }

    return params.toString()
  }

  goToPreviousDay() {
    if (!this.isSingleDay) return

    this.fromDate = this.fromDate.clone().subtract(1, "day")
    this.toDate = this.toDate.clone().subtract(1, "day")
    this.syncFlatpickr()
    this.updateNavigationState()
    this.requestData()
  }

  goToNextDay() {
    if (!this.isSingleDay || this.isToday) return

    this.fromDate = this.fromDate.clone().add(1, "day")
    this.toDate = this.toDate.clone().add(1, "day")
    this.syncFlatpickr()
    this.updateNavigationState()
    this.requestData()
  }

  dateRangeChanged(selectedDates) {
    if (selectedDates.length === 1) {
      this.fromDate = moment(selectedDates[0])
      this.toDate = moment(selectedDates[0])
      this.updateNavigationState()
      this.pendingDataRequest = true
    } else if (selectedDates.length === 2) {
      this.fromDate = moment(selectedDates[0])
      this.toDate = moment(selectedDates[1])
      this.updateNavigationState()
      this.pendingDataRequest = false
      this.requestData()
    }
  }

  handleDatePickerClose() {
    this.updateDisplayLabel()
    if (this.pendingDataRequest) {
      this.pendingDataRequest = false
      this.requestData()
    }
  }

  syncFlatpickr() {
    if (!this.flatpickrInstance) return

    this.flatpickrInstance.setDate([
      this.fromDate.toDate(),
      this.toDate.toDate()
    ], false)
    this.updateDisplayLabel()
  }

  updateNavigationState() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("d-none", !this.isSingleDay)
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("d-none", !this.isSingleDay || this.isToday)
    }
    this.updateDisplayLabel()
  }

  updateDisplayLabel() {
    if (!this.hasDateRangePickerTarget) return

    let text
    if (this.isToday) {
      text = `${helpers.t("vue.date_range_picker.today")}, ${this.fromDate.format("DD.MM.YYYY")}`
    } else if (this.isSingleDay) {
      text = this.fromDate.format("DD.MM.YYYY")
    } else {
      text = `${this.fromDate.format("DD.MM.YYYY")} \u2014 ${this.toDate.format("DD.MM.YYYY")}`
    }

    this.dateRangePickerTarget.value = text
    this.dateRangePickerTarget.size = text.length + 2
  }

  async requestData() {
    if (!this.remoteUrl) return

    this.chart?.showLoading()

    const { signal } = this.abortController
    const fetches = [
      fetch(this.remoteUrl, { method: "GET", headers: { Accept: "application/json" }, signal }).then(r => r.json())
    ]

    if (this.hasThingIdValue && this.showChargingCyclesValue) {
      const url = `/api/v1/things/${this.thingIdValue}/charging_cycles?${this.remoteTimeRangeParams}`
      fetches.push(fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal }).then(r => r.json()))
    }

    const [metricsResult, chargingCyclesResult] = await Promise.allSettled(fetches)

    if (signal.aborted) return

    if (metricsResult.status === "fulfilled") {
      this.updateChart(metricsResult.value)
    } else {
      console.error("[TimelineChart] Failed to fetch metrics data", metricsResult.reason)
      this.chart?.hideLoading()
    }

    if (chargingCyclesResult?.status === "fulfilled") {
      this.showChargingCycles(chargingCyclesResult.value)
    } else if (chargingCyclesResult?.status === "rejected") {
      console.error("[TimelineChart] Failed to fetch charging cycles", chargingCyclesResult.reason)
    }

    if (this.isToday && this.pollingTimerId === null) {
      this.pollingTimerId = setInterval(() => this.requestData(), constants.DAY_CHART_POLLING_INTERVAL)
    }

    if (!this.isToday) this.stopPolling()
  }

  stopPolling() {
    if (this.pollingTimerId !== null) {
      clearInterval(this.pollingTimerId)
      this.pollingTimerId = null
    }
  }

  renderSegmentedItem(params, api) {
    if (params.dataIndexInside >= params.dataInsideLength - 1) {
      return null
    }

    const style = api.style({
      stroke: api.visual("color"),
      fill: api.visual("color")
    })

    const lines = []
    const columns = params.encode.y
    const rowIndex = params.dataIndexInside

    for (let i = 0; i < columns.length; i += 1) {
      const colIndex = columns[i]
      const currentTime = api.value(0, rowIndex)
      const nextTime = api.value(0, rowIndex + 1)
      const currentValue = api.value(colIndex, rowIndex)
      const nextValue = api.value(colIndex, rowIndex + 1)

      if (isNaN(currentValue) || isNaN(nextValue)) {
        continue
      }

      const start = api.coord([currentTime, currentValue])
      const end = api.coord([nextTime, nextValue])

      lines.push({
        type: "line",
        style,
        shape: {
          x1: start[0], y1: start[1],
          x2: end[0], y2: end[1]
        }
      })
    }

    return {
      type: "group",
      children: lines
    }
  }

  updateChart(data) {
    const sortedData = Object.keys(data || {}).sort().reduce((newData, key) => {
      newData[key] = data[key]
      return newData
    }, {})

    const series = []
    const legend = []

    this.yAxes = []
    this.options.series = []
    this.options.legend.data = []

    this.chart?.clear()

    for (const [key, payload] of Object.entries(sortedData)) {
      if (!payload || typeof payload !== "object") {
        continue
      }

      if (!payload.info || !payload.info.label || payload.values.length < 2) {
        continue
      }

      const values = payload.values
      const info = payload.info
      const yAxisIndex = helpers.findYAxisIndexForSeriesKey(this.options.yAxis, key, info)

      if (info.array === true) {
        const seriesData = values.map((item) => ({
          name: key,
          value: (item.value && Object.keys(item.value).length > 0 ? [item.time, ...item.value] : [item.time, []]),
          unit: info.unit
        }))

        const numValues = Math.max(...seriesData.map((item) => item.value.length)) - 1
        const yColumnIndices = Array(numValues).fill().map((_, index) => index + 1)

        if (seriesData.length > 0 && seriesData[0].value.length < numValues) {
          const spacer = new Array(numValues).fill(null)
          seriesData[0].value = [seriesData[0].value[0], ...spacer]
        }

        series.push({
          type: "custom",
          id: key,
          name: info.label,
          renderItem: this.renderSegmentedItem.bind(this),
          data: seriesData,
          encode: {
            x: [0],
            y: yColumnIndices
          },
          yAxisIndex
        })
      } else {
        const lineSeries = {
          id: key,
          name: info.label,
          data: values.map((item) => ({
            name: key,
            value: [item.time, item.value],
            unit: info.unit
          })),
          type: "line",
          lineStyle: { width: 1.25 },
          showSymbol: false,
          connectNulls: false,
          yAxisIndex
        }

        if (info.key in DASHBOARD_COLORS) {
          lineSeries.color = DASHBOARD_COLORS[info.key]
        }

        series.push(lineSeries)
      }

      legend.push({ name: info.label })

      const axis = this.options.yAxis[yAxisIndex]
      this.yAxes.push({ key, position: axis.position, info })
    }

    if (sortedData.max_network_utilization && series.find((set) => set.id === "POWER_GRID")) {
      series.find((set) => set.id === "POWER_GRID").markLine = {
        silent: true,
        animation: false,
        symbol: "none",
        data: [{ yAxis: sortedData.max_network_utilization }],
        label: {
          formatter: helpers.t("vue.timeline_chart.max_grid"),
          position: "insideEndTop"
        }
      }
    }

    if (!this.options.legend.selected) {
      const selectedSeries = {}

      if (legend.length > 5) {
        let visible = 0
        for (let i = 0; i < legend.length; i += 1) {
          const item = series[i]
          if (visible < 5 && RELEVANT_SERIES.includes(item.id)) {
            visible += 1
            selectedSeries[item.name] = true
          } else {
            selectedSeries[item.name] = false
          }
        }
      } else {
        for (let i = 0; i < legend.length; i += 1) {
          const item = series[i]
          selectedSeries[item.name] = true
        }
      }

      this.options.legend.selected = selectedSeries
    }

    if (this.hasChargingCycles) {
      legend.push(this.t("vue.timeline_chart.charging_cycles"))
    }

    this.setXAxes()
    this.setYAxes()
    this.options.series = series
    this.options.legend.data = legend

    this.toggleNoDataText()
    this.renderChart()

    setTimeout(() => {
      if (!this.chart) {
        return
      }

      const legendComponent = this.chart.getModel()?.getComponent("legend")
      const legendView = legendComponent ? this.chart.getViewOfComponentModel(legendComponent) : null
      const legendHeight = legendView?.group?.getBoundingRect()?.height || 0
      this.options.grid.top = legendHeight + 20
      this.renderChart()
      this.chart.hideLoading()
      this.initialLoadComplete = true
    }, 100)
  }

  toggleNoDataText() {
    if (!this.hasNoDataTextTarget) return

    const hasData = this.options.series.length >= 1
    if (!hasData && this.noDataTextTarget.classList.contains("d-none")) this.chart?.clear()
    this.noDataTextTarget.classList.toggle("d-none", hasData)
  }

  setXAxes() {
    if (this.isSingleDay) {
      this.options.xAxis.minInterval = (3600 * 1000) / 2
      this.options.xAxis.maxInterval = 3600 * 1000 * 4
    } else {
      const rangeDays = this.toDate.diff(this.fromDate, "days") + 1
      const intervalHours = Math.max(4, Math.ceil((rangeDays * 24) / 5))
      this.options.xAxis.minInterval = 3600 * 1000 * intervalHours
      this.options.xAxis.maxInterval = 3600 * 1000 * intervalHours
    }
    this.options.xAxis.min = this.fromDate.clone().startOf("day").toISOString()
    this.options.xAxis.max = this.toDate.clone().add(1, "day").startOf("day").toISOString()
  }

  setYAxes() {
    this.options.yAxis.forEach((axis) => {
      axis.offset = 45
      axis.axisLine.show = false
    })

    let offsetLeft = 45
    let offsetRight = 45
    const displayedAxes = []

    this.yAxes.forEach((axis) => {
      if (!this.options.legend.selected || this.options.legend.selected[axis.info.label] === true) {
        const refAxis = this.options.yAxis[helpers.findYAxisIndexForSeriesKey(this.options.yAxis, axis.key, axis.info)]

        if (!displayedAxes.find((displayedAxis) => displayedAxis.id === refAxis.id)) {
          if (axis.position === "right") {
            if (offsetRight === 45) {
              refAxis.axisLine.show = true
            }
            refAxis.offset = offsetRight - 45
            offsetRight += 75
            this.options.grid.right = offsetRight
          } else if (axis.position === "left") {
            if (offsetLeft === 45) {
              refAxis.axisLine.show = true
            }
            refAxis.offset = offsetLeft - 45
            offsetLeft += 75
            this.options.grid.left = offsetLeft
          }
          displayedAxes.push(refAxis)
        }
      }
    })
  }

  showChargingCycles(data) {
    if (!Array.isArray(data) || data.length < 1) {
      return
    }

    this.hasChargingCycles = true
    this.options.grid.top = 80

    const lastEndTime = Math.max(...data.filter((cycle) => !!cycle.charging_end).map((cycle) => new Date(cycle.charging_end).getTime()))
    if (lastEndTime) {
      const lastCycle = data.filter((cycle) => !!cycle.charging_end).find((cycle) => new Date(cycle.charging_end).getTime() === lastEndTime)
      if (lastCycle) {
        this.extraValues = {
          consumed_energy_last_charging_cycle: lastCycle.consumed_energy,
          duration_last_charging_cycle: lastCycle.charging_time
        }
      }
    }

    const cyclesInRange = data.filter((cycle) => cycle.charging_start)
    const cycleMarkSeries = cyclesInRange.map((cycle) => {
      const millisecondsToHours = 1000 * 60 * 60
      let duration

      if (cycle.charging_end && cycle.charging_start) {
        duration = Math.round((new Date(cycle.charging_end) - new Date(cycle.charging_start)) / millisecondsToHours)
      } else {
        duration = Math.round((new Date() - new Date(cycle.charging_start)) / millisecondsToHours)
      }

      const durationText = `${this.t("vue.timeline_chart.duration")}: ${duration}`
      const consumedText = cycle.consumed_energy ? helpers.formatValueWithUnit(cycle.consumed_energy, "Wh") : ""
      const label = !cycle.charging_end ? this.t("vue.timeline_chart.current_cycle") : ""
      const consumption = cycle.consumed_energy ? `${this.t("vue.timeline_chart.consumption")}: ${consumedText}` : ""
      const name = `${label}\n${durationText}h\n${consumption}`

      return [
        { name, xAxis: new Date(cycle.charging_start) },
        { xAxis: cycle.charging_end ? new Date(cycle.charging_end) : new Date() }
      ]
    })

    this.options.series.push({
      name: this.t("vue.timeline_chart.charging_cycles"),
      color: "green",
      type: "line",
      symbolSize: 3,
      markArea: {
        silent: false,
        data: [].concat(cycleMarkSeries),
        label: {
          show: false,
          position: "top",
          fontSize: 10
        }
      }
    })

    this.renderChart()
  }

  legendChanged(event) {
    if (this.options.legend.selected) {
      this.options.legend.selected = event.selected
    }
    this.setYAxes()
    this.renderChart()
  }

  formatXAxisTime(value) {
    const format = this.isSingleDay ? "HH:mm" : "DD.MM HH:mm"
    return helpers.formatDateTime(value, format)
  }

  formatAxisPointerLabel(param) {
    if (param.axisDimension === "y") {
      const unit = helpers.findUnitForAxisIndex(this.options.yAxis, param.axisIndex)
      return unit ? helpers.formatValueWithUnit(param.value, unit) : param.value.toFixed(2)
    }

    if (param.axisDimension === "x") {
      return helpers.formatDateTime(param.value)
    }

    return param.value
  }

  renderChart() {
    this.chart?.setOption(this.options, true)
  }

  t(scope, options = {}) {
    return helpers.t(scope, options)
  }

  buildChartOptions() {
    return {
      grid: {
        left: 45,
        right: 45,
        top: 80
      },
      legend: {
        show: true,
        data: [],
        top: "top"
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            show: true,
            formatter: (param) => this.formatAxisPointerLabel(param)
          }
        },
        formatter: (params) => {
          const time = helpers.formatDateTime(params[0].data.value[0])
          const seriesTooltips = params.map((series) => {
            const yIndices = series.encode?.y || []
            const segmentedValues = yIndices.length > 1
            const unit = series?.data?.unit

            if (segmentedValues) {
              return yIndices.map((index) => {
                const value = series.value[index]
                const formattedValue = unit ? helpers.formatValueWithUnit(value, unit) : (typeof value === "number" ? value.toFixed(1) : value)
                return `${series.marker}${series.seriesName} ${index}: ${formattedValue}`
              }).join("<br/>")
            }

            const value = series.value[1]
            const formattedValue = unit ? helpers.formatValueWithUnit(value, unit) : (typeof value === "number" ? value.toFixed(1) : value)
            return `${series.marker}${series.seriesName}: ${formattedValue}`
          })

          return `${time}<br/>${seriesTooltips.join("<br/>")}`
        }
      },
      xAxis: {
        type: "time",
        splitLine: { show: false },
        boundaryGap: [0, 0],
        axisLabel: {
          formatter: (value) => this.formatXAxisTime(value)
        }
      },
      yAxis: [
        makeYAxis("default",       "left"),
        makeYAxis("percentage",    "left",  "{value}%"),
        makeYAxis("ampere",        "left",  (v) => helpers.formatValueWithUnit(v, "A")),
        makeYAxis("voltage",       "right", (v) => helpers.formatValueWithUnit(v, "V")),
        makeYAxis("power",         "right", (v) => helpers.formatValueWithUnit(v, "W")),
        makeYAxis("frequency",     "right", (v) => helpers.formatValueWithUnit(v, "Hz")),
        makeYAxis("apparent_power","left",  (v) => helpers.formatValueWithUnit(v, "VA")),
        makeYAxis("reactive_power","right", (v) => helpers.formatValueWithUnit(v, "VAr")),
        makeYAxis("energy",        "right", (v) => helpers.formatValueWithUnit(v, "Wh")),
        makeYAxis("temperature",   "left",  "{value}°C"),
        makeYAxis("operationTime", "left",  "{value}h"),
      ],
      dataZoom: [{ start: 0, end: 100 }, { type: "inside" }],
      series: []
    }
  }
}
